import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// import { useLanguage } from '@/state/language/LanguageProvider';
import { resourcesService, Resource, ResourceAccessLog } from '@/services/resources';
import { 
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  DocumentIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  TagIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// =====================================================
// RESOURCE VIEW PAGE
// TMOS Platform - Individual Resource View
// =====================================================
// Component: ResourceViewPage
// Description: Detailed view of a single resource with access logs
// Author: TMOS Development Team
// Date: September 30, 2025
// =====================================================

export const ResourceViewPage: React.FC = () => {
  // const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [resource, setResource] = useState<Resource | null>(null);
  const [accessLogs, setAccessLogs] = useState<ResourceAccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAccessLogs, setShowAccessLogs] = useState(false);

  // =====================================================
  // DATA LOADING
  // =====================================================

  useEffect(() => {
    if (id) {
      loadResource();
    }
  }, [id]);

  const loadResource = async () => {
    try {
      setLoading(true);
      setError(null);

      const [resourceData, logsData] = await Promise.all([
        resourcesService.getResource(id!),
        resourcesService.getAccessLogs(id!)
      ]);

      setResource(resourceData);
      setAccessLogs(logsData);

      // Log this view
      await resourcesService.logAccess(id!, 'view');
    } catch (err: any) {
      setError(err.message || 'Failed to load resource');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RESOURCE ACTIONS
  // =====================================================

  const handleOpenResource = async () => {
    if (!resource) return;

    try {
      if (resource.resource_type === 'link' && resource.url) {
        try {
          await resourcesService.logAccess(resource.id, 'click');
        } catch (logErr) {
          console.warn('Failed to log access:', logErr);
        }
        window.open(resource.url, '_blank');
      } else if (resource.resource_type === 'document' && resource.file_path) {
        // Handle document download/viewing
        const downloadUrl = `/api/v1/resources/${resource.id}/download`;
        
        try {
          await resourcesService.logAccess(resource.id, 'download');
        } catch (logErr) {
          console.warn('Failed to log access:', logErr);
        }
        
        // Create a hidden link element for download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = resource.file_name || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      console.error('Error opening resource:', err);
      setError(err.message || 'Failed to open resource');
    }
  };

  const handleShare = async () => {
    if (!resource) return;

    try {
      // Try to log access, but don't fail if it doesn't work
      try {
        await resourcesService.logAccess(resource.id, 'view');
      } catch (logErr) {
        console.warn('Failed to log access:', logErr);
      }
      
      const shareUrl = window.location.href;
      const shareText = `${resource.title}${resource.description ? ` - ${resource.description}` : ''}`;
      
      if (navigator.share && navigator.canShare && navigator.canShare()) {
        try {
          await navigator.share({
            title: resource.title,
            text: shareText,
            url: shareUrl
          });
        } catch (shareErr) {
          // If native share fails, fall back to clipboard
          await navigator.clipboard.writeText(shareUrl);
          alert('Link copied to clipboard');
        }
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard');
      }
    } catch (err) {
      console.error('Error sharing resource:', err);
      // Final fallback - just copy the URL
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard');
      } catch (clipboardErr) {
        alert('Unable to copy link. Please copy the URL manually.');
      }
    }
  };



  // =====================================================
  // RENDER HELPERS
  // =====================================================

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getResourceTypeIcon = (type: string) => {
    return type === 'link' ? (
      <ArrowTopRightOnSquareIcon className="h-6 w-6 text-blue-600" />
    ) : (
      <DocumentIcon className="h-6 w-6 text-green-600" />
    );
  };

  const getResourceTypeLabel = (type: string) => {
    return type === 'link' ? 'External Link' : 'Document';
  };

  const isResourceActive = (resource: Resource) => {
    const now = new Date();
    const activeFrom = new Date(resource.active_from);
    const activeUntil = resource.active_until ? new Date(resource.active_until) : null;
    
    // Check if resource is within active period
    const isWithinPeriod = activeFrom <= now && (!activeUntil || activeUntil >= now);
    
    // Resource is active only if it's within the time period AND is_active is true
    return isWithinPeriod && (resource.is_active !== false);
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error || 'Resource not found'}</p>
        <button
          onClick={() => navigate('/resources')}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Back to Resources
        </button>
      </div>
    );
  }

  const isActive = isResourceActive(resource);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/resources')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Back to Resources</span>
        </button>
        
      </div>

      {/* Resource Details */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          {/* Title and Type */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              {getResourceTypeIcon(resource.resource_type)}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{resource.title}</h1>
                <p className="text-sm text-gray-500">{getResourceTypeLabel(resource.resource_type)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs ${
                isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Description */}
          {resource.description && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
              <p className="text-gray-600">{resource.description}</p>
            </div>
          )}

          {/* Resource Content */}
          <div className="mb-6">
            {resource.resource_type === 'link' && resource.url && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 text-blue-600" />
                    <span className="text-blue-600 font-medium">External Link</span>
                  </div>
                  <button
                    onClick={handleOpenResource}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Open Link
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2 break-all">{resource.url}</p>
              </div>
            )}

            {resource.resource_type === 'document' && resource.file_path && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <DocumentIcon className="h-5 w-5 text-green-600" />
                    <span className="text-green-600 font-medium">Document</span>
                  </div>
                  <button
                    onClick={handleOpenResource}
                    className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                </div>
                {resource.file_name && (
                  <p className="text-sm text-gray-600 mt-2">{resource.file_name}</p>
                )}
                {resource.file_size && (
                  <p className="text-xs text-gray-500 mt-1">
                    Size: {(resource.file_size / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1 break-all">
                  File: {resource.file_path}
                </p>
              </div>
            )}
          </div>

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <TagIcon className="h-4 w-4 mr-1" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {resource.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {tag.name}
                    {tag.is_predefined && <span className="ml-1">★</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                Active Period
              </h3>
              <div className="text-sm text-gray-600">
                <p>From: {formatDate(resource.active_from)}</p>
                {resource.active_until && (
                  <p>Until: {formatDate(resource.active_until)}</p>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                Created By
              </h3>
              <p className="text-sm text-gray-600">
                {resource.created_by_name || 'Unknown User'}
              </p>
              <p className="text-xs text-gray-500">
                {formatDate(resource.created_at)}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4 pt-4 border-t">
            {resource.resource_type === 'link' && resource.url && (
              <button
                onClick={handleOpenResource}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                <span>Open Link</span>
              </button>
            )}
            
            {resource.resource_type === 'document' && resource.file_path && (
              <button
                onClick={handleOpenResource}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Download</span>
              </button>
            )}
            
            <button
              onClick={handleShare}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              <ShareIcon className="h-4 w-4" />
              <span>Share</span>
            </button>
            
            <button
              onClick={() => setShowAccessLogs(!showAccessLogs)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              <EyeIcon className="h-4 w-4" />
              <span>View Access Logs</span>
            </button>
          </div>
        </div>
      </div>

      {/* Access Logs */}
      {showAccessLogs && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2" />
              Access Logs
            </h3>
            
            {accessLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {accessLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.user_name || 'Anonymous'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(log.access_timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.ip_address || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No access logs found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
